import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { api } from "../../lib/api";
import { formatInr, formatInrLoose } from "../../lib/currency";
import { useAuth } from "../../providers/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ReportsDateRangeFilterBar } from "../components/reports/ReportsDateRangeFilterBar";
import { TableLayout, type TableColumn } from "../../components/dashboard";

type ProductPerformanceRow = {
  product_id: string;
  product_name: string;
  total_qty_sold: string;
  total_revenue: string;
};

function parseMoney(v: string) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function ReportsProductsPage() {
  const { token } = useAuth();

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const [rows, setRows] = useState<ProductPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const res = await api.reports.getProductPerformance({
          token,
          filters: { from_date: fromDate, to_date: toDate },
        });
        if (cancelled) return;
        setRows((res.data as ProductPerformanceRow[]) ?? []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load product performance");
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, fromDate, toDate]);

  const totals = useMemo(() => {
    const totalQty = rows.reduce((s, r) => s + Number(r.total_qty_sold || 0), 0);
    const totalRevenue = rows.reduce((s, r) => s + parseMoney(r.total_revenue || "0"), 0);
    return { totalQty, totalRevenue };
  }, [rows]);

  const chartData = useMemo(() => {
    // LineChart expects ordered points; we keep the API's revenue-desc order and show the top N.
    return rows.slice(0, 12).map((r) => ({
      product_name: r.product_name,
      total_revenue: parseMoney(r.total_revenue),
      total_qty_sold: Number(r.total_qty_sold || 0),
    }));
  }, [rows]);

  const columns: TableColumn<ProductPerformanceRow>[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product",
        render: (r) => (
          <div className="min-w-0 leading-tight">
            <div className="font-medium">{r.product_name}</div>
            <div className="font-mono text-xs text-muted-foreground">{r.product_id}</div>
          </div>
        ),
      },
      {
        key: "qty",
        header: "Qty sold",
        render: (r) => (
          <span className="block text-right tabular-nums">{Number(r.total_qty_sold || 0).toLocaleString()}</span>
        ),
      },
      {
        key: "rev",
        header: "Revenue",
        render: (r) => (
          <span className="block text-right tabular-nums">{formatInr(parseMoney(r.total_revenue || "0"))}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Product performance</h1>
          <p className="text-muted-foreground">Top products by revenue (optionally filtered by date).</p>
        </div>
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <ReportsDateRangeFilterBar
            fromDate={fromDate}
            toDate={toDate}
            onChange={({ fromDate: nextFrom, toDate: nextTo }) => {
              setFromDate(nextFrom);
              setToDate(nextTo);
            }}
          />
          <div className="sm:self-end">
            <Link
              to="/reports"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Back to hub
            </Link>
          </div>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{rows.length}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total quantity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{totals.totalQty.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{formatInrLoose(totals.totalRevenue)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div>
            <CardTitle>Revenue & quantity (top products)</CardTitle>
            <CardDescription>Line metrics are shown across the top products by revenue for the selected range.</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground">Loading chart...</div>
          ) : chartData.length === 0 ? (
            <div className="text-muted-foreground">No product performance data for the selected range.</div>
          ) : (
            <div className="h-[260px] min-h-[220px] w-full min-w-0 md:h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="product_name"
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    yAxisId="left"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatInrLoose(Number(v))}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => Number(v).toLocaleString("en-IN")}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === "total_revenue") return [formatInrLoose(value), "Revenue"];
                      if (name === "total_qty_sold") return [`${value.toLocaleString("en-IN")}`, "Qty sold"];
                      return [value, name];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <Line yAxisId="left" type="linear" dataKey="total_revenue" stroke="#2563eb" dot={false} strokeWidth={2} />
                  <Line yAxisId="right" type="linear" dataKey="total_qty_sold" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Products table</CardTitle>
              <CardDescription>Sorted descending by revenue.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No products found.</div>
          ) : (
            <div className="min-w-0 rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

